import { LoginCallback } from "@okta/okta-react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthLayout } from "@/layouts/auth-layout";
import { MainLayout } from "@/layouts/main-layout";
import { Login } from "@/pages/login";
import { ProtectedRoute } from "./protected-route";

const Tenants = lazy(() =>
	import("@/pages/tenants").then((m) => ({ default: m.Tenants })),
);

const CreateTenant = lazy(() =>
	import("@/pages/create-tenant").then((m) => ({ default: m.CreateTenant })),
);

const LoadingFallback = () => (
	<div className="flex min-h-screen items-center justify-center">
		<div className="text-center">
			<div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-current border-r-transparent border-solid align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]" />
			<p className="text-gray-600 text-lg">Loading...</p>
		</div>
	</div>
);

export const AppRoutes = () => {
	return (
		<Suspense fallback={<LoadingFallback />}>
			<Routes>
				{/* ================== PUBLIC ROUTES ================== */}
				{/* Login page with AuthLayout */}
				<Route element={<AuthLayout />}>
					<Route path="/login" element={<Login />} />
				</Route>

				{/* OAuth callback without any layout */}
				<Route path="/login/callback" element={<LoginCallback />} />

				{/* ================== PRIVATE ROUTES ================== */}
				{/* All private routes use MainLayout and are wrapped with ProtectedRoute */}
				<Route element={<MainLayout />}>
					{/* Default route - redirect to tenants */}
					<Route
						path="/"
						element={
							<ProtectedRoute>
								<Navigate to="/tenants" replace />
							</ProtectedRoute>
						}
					/>

					{/* Tenants routes */}
					<Route path="/tenants">
						{/* Default tenants page */}
						<Route
							index
							element={
								<ProtectedRoute>
									<Tenants />
								</ProtectedRoute>
							}
						/>
						{/* Create tenant page */}
						<Route
							path="create"
							element={
								<ProtectedRoute>
									<CreateTenant />
								</ProtectedRoute>
							}
						/>
					</Route>
				</Route>

				{/* ================== CATCH-ALL ROUTE ================== */}
				{/* Redirect any unknown routes to home (which then redirects to /tenants) */}
				<Route path="*" element={<Navigate to="/" replace />} />
			</Routes>
		</Suspense>
	);
};

export const PUBLIC_ROUTES = ["/login", "/login/callback"];

export const isPublicRoute = (path: string): boolean => {
	return PUBLIC_ROUTES.some((publicPath) => path.startsWith(publicPath));
};
